import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useDailyLeaderboard, useDailyQuest } from "../api/queries";
import { EmptyState, Skeleton } from "../components/ui";
import { formatScore } from "../lib/format";
import { useAuthStore } from "../stores/authStore";
import { usePageMeta } from "../lib/usePageMeta";

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function LeaderboardPage() {
  const { t } = useTranslation();
  usePageMeta({ title: t("leaderboard.title"), description: t("seo.leaderboard") });
  const [date, setDate] = useState(todayISO());
  const user = useAuthStore((state) => state.user);

  const leaderboard = useDailyLeaderboard(date);
  const daily = useDailyQuest();

  const entries = leaderboard.data?.entries ?? [];
  const me = leaderboard.data?.me;
  const meInTop = entries.some((entry) => entry.user?.id === me?.user?.id);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-sans text-3xl text-ink-50">{t("leaderboard.title")}</h1>
          <p className="mt-1 text-sm text-ink-400">
            {t("leaderboard.lead")}
          </p>
        </div>

        <label className="shrink-0">
          <span className="label">{t("leaderboard.date")}</span>
          <input
            type="date"
            className="field"
            value={date}
            max={todayISO()}
            onChange={(event) => setDate(event.target.value)}
          />
        </label>
      </div>

      {daily.data && date === todayISO() && (
        <Link
          to={`/quests/${daily.data.id}`}
          className="panel flex items-center justify-between gap-4 p-4 transition-colors
                     hover:border-amber-400/40"
        >
          <div>
            <span className="chip border-amber-400/40 text-amber-300">{t("leaderboard.today")}</span>
            <div className="mt-1.5 font-sans text-xl text-ink-50">{daily.data.title}</div>
          </div>
          <span className="btn-primary shrink-0">{t("landing.play")}</span>
        </Link>
      )}

      {leaderboard.isPending && <Skeleton className="h-64" />}

      {leaderboard.data && entries.length === 0 && (
        <EmptyState
          title={t("leaderboard.emptyTitle")}
          description={t("leaderboard.emptyText")}
        />
      )}

      {entries.length > 0 && (
        <div className="panel divide-y divide-ink-800 overflow-hidden">
          {entries.map((entry) => (
            <div
              key={`${entry.user?.id}-${entry.rank}`}
              className={`flex items-center gap-4 px-4 py-3 ${
                entry.user?.id === user?.id ? "bg-amber-400/10" : ""
              }`}
            >
              <RankBadge rank={entry.rank ?? 0} />
              <Link
                to={`/players/${entry.user?.id}`}
                className="min-w-0 flex-1 truncate text-sm text-ink-100 hover:text-amber-300"
              >
                {entry.user?.username}
              </Link>
              <span className="tabular-nums text-lg text-amber-300">
                {formatScore(entry.totalScore ?? 0)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Своя строка показывается отдельно, если игрок не попал в топ. */}
      {me && !meInTop && (
        <div className="panel flex items-center gap-4 border-amber-400/30 px-4 py-3">
          <RankBadge rank={me.rank ?? 0} />
          <span className="min-w-0 flex-1 truncate text-sm text-ink-100">
            {t("leaderboard.yourResult", { name: me.user?.username })}
          </span>
          <span className="tabular-nums text-lg text-amber-300">
            {formatScore(me.totalScore ?? 0)}
          </span>
        </div>
      )}
    </div>
  );
}

function RankBadge({ rank }: { rank: number }) {
  const medal = rank === 1 ? "text-amber-300" : rank === 2 ? "text-ink-200" : rank === 3 ? "text-amber-600" : "text-ink-500";

  return (
    <span className={`w-8 shrink-0 text-right tabular-nums text-sm ${medal}`}>{rank}</span>
  );
}
